import { useMemo } from 'react';
import { looksLikeVeo } from '../core/parsers';
import { formatClock, formatVideoTime, periodName } from '../core/time';
import { useReel } from '../state/reel';
import { useApp } from '../state/store';
import { Button, Chip, Field, Icon, Panel, Segmented, TextArea } from '../ui';

interface Row {
  line: number;
  kind: 'moment' | 'skipped' | 'ignored';
  period: string;
  time: string;
  length: string;
  note: string;
  label: string;
}

export function TimestampsScreen() {
  const { project, update, setStep, file, info } = useApp();
  const { parsed, usesClock } = useReel();
  const isVeo = project.source === 'veo';

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = parsed.moments.map((m) => ({
      line: m.line,
      kind: 'moment',
      period: m.period ? periodName(m.period) : '—',
      time: m.period ? formatClock(m.timeSec) : formatVideoTime(m.timeSec),
      length: `${m.durationSec ?? project.defaultLength} s`,
      note: m.lengthSource === 'default' ? 'default' : m.lengthSource === 'range' ? 'range' : '',
      label: m.label || '—',
    }));
    for (const s of parsed.skipped) out.push({ line: s.line, kind: 'skipped', period: '—', time: '—', length: 'Skipped', note: '', label: `“${s.text}”: ${s.reason.toLowerCase()}` });
    for (const s of parsed.ignored) out.push({ line: s.line, kind: 'ignored', period: '—', time: '—', length: 'Ignored', note: '', label: s.text });
    return out.sort((a, b) => a.line - b.line);
  }, [parsed, project.defaultLength]);

  const count = parsed.moments.length;
  const periods = Object.entries(parsed.periodCounts).filter(([p]) => p !== '0');
  const suggestVeo = !isVeo && looksLikeVeo(project.text);
  const next = usesClock ? 'sync' : 'clips';

  return (
    <div className="app-grid-left">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {file && info && (
          <Panel aria-label="Match video">
            <div className="app-row-between">
              <h2 className="app-label">Match video</h2>
              <Chip tone="pitch" icon="check">
                Loaded
              </Chip>
            </div>
            <span style={{ fontSize: 16, fontWeight: 600, overflowWrap: 'anywhere' }}>{file.name}</span>
            <span className="time text-small muted">
              {formatVideoTime(info.duration)} · {info.width} × {info.height}
            </span>
            <span className="text-small muted app-row" style={{ gap: 6 }}>
              <Icon name="lock" size={16} />
              Read in place, not uploaded
            </span>
          </Panel>
        )}
        <Panel aria-labelledby="tips-title">
          <h2 id="tips-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            How long is each clip?
          </h2>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, lineHeight: 1.5, color: 'var(--ink-muted)' }}>
            <li>
              Veo lines carry their length: <span className="time" style={{ color: 'var(--ink)' }}>02:5914s</span> is 14 s from 02:59.
            </li>
            <li>
              In your own list, add a length (<span className="time" style={{ color: 'var(--ink)' }}>23:41 20s</span>) or a range (<span className="time" style={{ color: 'var(--ink)' }}>31:18-31:40</span>).
            </li>
            <li>Lines without one use the default clip length. Padding is added around every clip at export.</li>
          </ul>
        </Panel>
        <p className="text-small muted" style={{ margin: 0 }}>
          Rather watch and mark?{' '}
          <button type="button" onClick={() => { update({ method: 'live' }); setStep('mark'); }} style={{ background: 'none', border: 0, padding: 0, color: 'var(--link)', fontWeight: 500, cursor: 'pointer' }}>
            Mark it live instead
          </button>
        </p>
        <div className="app-row">
          <Button icon="arrow-left" onClick={() => setStep('start')}>
            Back
          </Button>
          <Button variant="primary" icon="arrow-right" iconAfter disabled={count === 0} onClick={() => setStep(next)} style={{ flexGrow: 1 }}>
            {usesClock ? 'Continue to kickoffs' : 'Continue to clips'}
          </Button>
        </div>
      </div>

      <Panel aria-labelledby="add-title" style={{ gap: 20 }}>
        <div className="app-row-between">
          <h1 id="add-title" className="app-title">
            Add your timestamps
          </h1>
          <Chip tone={usesClock ? 'neutral' : 'cue'}>{usesClock ? 'Kickoff sync next' : 'No kickoff sync needed'}</Chip>
        </div>
        <div data-tour="source" className="app-row" style={{ flexWrap: "wrap", alignItems: "flex-end", gap: 24 }}>
          <Segmented
            label="Where the timestamps come from"
            value={project.source}
            onChange={(v) => update({ source: v })}
            options={[
              { value: 'veo', label: 'Paste from Veo', icon: 'list' },
              { value: 'list', label: 'Paste any list' },
            ]}
          />
          {!isVeo && (
            <>
              <div className="hl-field">
                <span className="hl-label">Times in the list are</span>
                <Segmented
                  label="Times in the list are"
                  value={project.timeBase}
                  onChange={(v) => update({ timeBase: v })}
                  options={[
                    { value: 'match', label: 'Match clock' },
                    { value: 'video', label: 'Video time' },
                  ]}
                />
              </div>
              <Field
                label="Default clip length"
                type="number"
                min={1}
                max={120}
                suffix="s"
                value={project.defaultLength}
                onChange={(e) => update({ defaultLength: Math.max(1, Number(e.target.value) || 10) })}
                style={{ width: 140 }}
              />
            </>
          )}
        </div>
        {suggestVeo && (
          <div className="app-banner app-banner-cue">
            <Icon name="info" />
            <span style={{ flexGrow: 1 }}>This looks like a Veo list.</span>
            <Button size="sm" onClick={() => update({ source: 'veo' })}>
              Read it as Veo
            </Button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
          <TextArea
            data-tour="paste"
            label={isVeo ? 'Veo list' : 'Your list'}
            rows={24}
            value={project.text}
            onChange={(e) => update({ text: e.target.value })}
            placeholder={isVeo ? '1st period\n02:5914s\n04:0019s\n2nd period\n50:1212s' : '1st half\n12:04\n23:41 20s Goal\n31:18-31:40 Save\n2nd half\n58:47 Header'}
            hint={isVeo ? 'Paste the list as it comes from Veo.' : 'One moment per line: a time, then a length, a range or a label.'}
          />
          <div className="ptable" aria-live="polite" data-tour="found">
            <div className="app-row" style={{ flexWrap: 'wrap', gap: 8, paddingBottom: 12 }}>
              <span className="app-label" style={{ marginRight: 4 }}>
                What we found
              </span>
              <Chip tone={count ? 'signal' : 'neutral'} icon={count ? 'check' : undefined}>
                {count} {count === 1 ? 'timestamp' : 'timestamps'}
              </Chip>
              {periods.map(([p, n]) => (
                <Chip key={p}>
                  {periodName(Number(p))} period · {n}
                </Chip>
              ))}
              {parsed.skipped.length > 0 && (
                <Chip tone="warning" icon="alert">
                  {parsed.skipped.length} {parsed.skipped.length === 1 ? 'line' : 'lines'} skipped
                </Chip>
              )}
              {parsed.duplicates > 0 && <Chip>{parsed.duplicates} duplicates removed</Chip>}
            </div>
            <div className="ptable-row ptable-head">
              <span>Line</span>
              <span>Period</span>
              <span>Time</span>
              <span>Length</span>
              <span>Label</span>
            </div>
            <div className="ptable-body">
              {rows.length === 0 && <p className="text-small muted" style={{ padding: 12 }}>Paste your list on the left. Each line is read as you type.</p>}
              {rows.map((r) => (
                <div key={`${r.kind}${r.line}`} className={`ptable-row${r.kind === 'skipped' ? ' is-warning' : r.kind === 'ignored' ? ' is-ignored' : ''}`}>
                  <span className="time text-small">{r.line}</span>
                  <span>{r.period}</span>
                  <span className="time">{r.time}</span>
                  <span>
                    <span className="time">{r.length}</span> {r.note && <span className="text-small muted">{r.note}</span>}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}
